import type {
  ImportPgaFlagDefinition,
  ImportPgaDefinitionField,
} from './import-service';
import { PGA_FLAG_SCHEMAS, type PgaCode, type PgaField } from './pga-schema';

// ProductPGAFlag's existing payload validator accepts namespaced keys that
// start with a letter, so provenance can live in values without a migration.
export const PGA_LINKED_HIT_STORAGE_KEY = 'metadata.linkedPgaHit';

export interface PgaHitOption {
  id: string;
  code: PgaCode;
  listName: string;
  agency: string;
  raw: Record<string, string | number>;
}

export interface LinkedPgaHitSnapshot {
  id: string;
  code: PgaCode;
  listName: string;
  agency: string;
  program?: string;
  matchedHts?: string;
  sourceUrl?: string;
  screenedAt?: string;
  linkedAt: string;
}

export interface PgaHitPrefillField {
  key: string;
  label: string;
  value: string | number;
}

export interface PgaHitPrefill {
  values: Record<string, unknown>;
  repeaters: Record<string, Record<string, unknown>[]>;
  fields: PgaHitPrefillField[];
}

const hasValue = (value: unknown): value is string | number =>
  (typeof value === 'string' && Boolean(value.trim())) ||
  (typeof value === 'number' && Number.isFinite(value));

const normalizeKey = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const rawValueMap = (
  raw: Record<string, string | number>,
): Map<string, string | number> => {
  const result = new Map<string, string | number>();
  Object.entries(raw).forEach(([key, value]) => {
    if (hasValue(value)) result.set(normalizeKey(key), value);
  });
  return result;
};

const firstRawValue = (
  raw: Record<string, string | number>,
  keys: string[],
): string | number | undefined => {
  const values = rawValueMap(raw);
  for (const key of keys) {
    const value = values.get(normalizeKey(key));
    if (hasValue(value)) return value;
  }
  return undefined;
};

const stableRawFingerprint = (raw: Record<string, string | number>): string => {
  const source = Object.entries(raw)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, value]) => `${key}:${String(value)}`)
    .join('|');
  let hash = 2166136261;
  for (let index = 0; index < source.length; index += 1) {
    hash ^= source.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
};

export const buildPgaHitId = (
  code: PgaCode,
  raw: Record<string, string | number>,
): string => {
  const recordId = firstRawValue(raw, [
    'BITE Record ID',
    'Record ID',
    '_id',
    'id',
  ]);
  return `${code}:${recordId ? String(recordId) : stableRawFingerprint(raw)}`;
};

const normalizedFieldValue = (
  field: ImportPgaDefinitionField,
  value: string | number,
): string | number => {
  if (field.inputType !== 'yesno') return value;
  const normalized = String(value).trim().toLowerCase();
  if (['yes', 'y', 'true', '1'].includes(normalized)) return 'Yes';
  if (['no', 'n', 'false', '0'].includes(normalized)) return 'No';
  return value;
};

const fieldAliases = (
  definition: ImportPgaFlagDefinition,
  field: ImportPgaDefinitionField,
): string[] => {
  const aliases = [field.header];
  const normalizedHeader = normalizeKey(field.header);
  const prefixes = [definition.sourceCode, definition.code.replace(/_/g, ' ')]
    .map(normalizeKey)
    .filter(Boolean);
  prefixes.forEach((prefix) => {
    if (normalizedHeader.startsWith(`${prefix} `)) {
      aliases.push(normalizedHeader.slice(prefix.length + 1));
    }
  });
  return aliases;
};

const setNestedRepeaterValue = (
  repeaters: Record<string, Record<string, unknown>[]>,
  target: Extract<
    NonNullable<ImportPgaDefinitionField['uiTarget']>,
    { kind: 'repeater' }
  >,
  value: string | number,
) => {
  const items = [...(repeaters[target.group] ?? [])];
  while (items.length <= target.index) items.push({});
  const item = { ...items[target.index] };
  if (!target.path?.length) {
    item[target.key] = value;
  } else {
    const rootCandidate = item[target.key];
    const root =
      rootCandidate && typeof rootCandidate === 'object'
        ? { ...(rootCandidate as Record<string, unknown>) }
        : {};
    let cursor = root;
    target.path.forEach((segment, index) => {
      if (index === target.path!.length - 1) {
        cursor[segment] = value;
        return;
      }
      const child = cursor[segment];
      const next =
        child && typeof child === 'object'
          ? { ...(child as Record<string, unknown>) }
          : {};
      cursor[segment] = next;
      cursor = next;
    });
    item[target.key] = root;
  }
  items[target.index] = item;
  repeaters[target.group] = items;
};

const isComplianceDecision = (field: ImportPgaDefinitionField): boolean =>
  field.uiTarget?.kind === 'value' &&
  ['declare.declare', 'declare.tscaCertification'].includes(field.uiTarget.key);

export const buildPgaHitPrefill = (
  hit: PgaHitOption,
  definition: ImportPgaFlagDefinition | null,
): PgaHitPrefill => {
  const values: Record<string, unknown> = {};
  const repeaters: Record<string, Record<string, unknown>[]> = {};
  const fields: PgaHitPrefillField[] = [];
  const rawValues = rawValueMap(hit.raw);

  if (definition) {
    definition.fields.forEach((field) => {
      // A screening match establishes possible applicability, not the user's
      // final declaration or TSCA certification decision.
      if (isComplianceDecision(field)) return;
      const value = fieldAliases(definition, field)
        .map((alias) => rawValues.get(normalizeKey(alias)))
        .find(hasValue);
      if (!hasValue(value)) return;
      const normalized = normalizedFieldValue(field, value);
      values[field.storageKey] = normalized;
      if (field.uiTarget?.kind === 'value') {
        values[field.uiTarget.key] = normalized;
      } else if (field.uiTarget?.kind === 'repeater') {
        setNestedRepeaterValue(repeaters, field.uiTarget, normalized);
      }
      fields.push({
        key: field.storageKey,
        label: field.header,
        value: normalized,
      });
    });
  }

  // Some screening records carry a generic Program value rather than the
  // workbook's agency-prefixed header. Only apply it when it exactly matches
  // one of the selected flag's allowed values; descriptive program names are
  // deliberately left as provenance metadata.
  const rawProgram = firstRawValue(hit.raw, ['Program']);
  if (hasValue(rawProgram)) {
    const schema = PGA_FLAG_SCHEMAS[hit.code];
    const candidates: Array<{ key: string; field: PgaField }> = [];
    schema.subGroups.forEach((group) => {
      group.fields.forEach((field) => {
        const key = field.storageKey ?? `${group.key}.${field.key}`;
        if (field.options?.includes(String(rawProgram))) {
          candidates.push({ key, field });
        }
      });
    });
    if (candidates.length === 1 && values[candidates[0].key] === undefined) {
      values[candidates[0].key] = rawProgram;
      fields.push({
        key: candidates[0].key,
        label: candidates[0].field.label,
        value: rawProgram,
      });
    }
  }

  // Avoid counting canonical aliases twice when a definition field stored
  // both its workbook value and its visible editor target.
  const uniqueFields = fields.filter(
    (field, index) =>
      fields.findIndex((candidate) => candidate.key === field.key) === index,
  );
  return { values, repeaters, fields: uniqueFields };
};

export const createLinkedPgaHitSnapshot = (
  hit: PgaHitOption,
): LinkedPgaHitSnapshot => {
  const optional = (keys: string[]): string | undefined => {
    const value = firstRawValue(hit.raw, keys);
    return hasValue(value) ? String(value) : undefined;
  };
  return {
    id: hit.id,
    code: hit.code,
    listName: hit.listName,
    agency: hit.agency,
    program: optional(['Program', 'Program Code']),
    matchedHts: optional(['HS Code', 'HTS Code']),
    sourceUrl: optional(['Regulation URL', 'Source URL', 'Source URLs', 'URL']),
    screenedAt: optional([
      'Date Updated on BITE Platform',
      'Date Added',
      'updated_at',
    ]),
    linkedAt: new Date().toISOString(),
  };
};

export const getLinkedPgaHitSnapshot = (
  values: Record<string, unknown>,
): LinkedPgaHitSnapshot | null => {
  const candidate = values[PGA_LINKED_HIT_STORAGE_KEY];
  if (!candidate || typeof candidate !== 'object') return null;
  const snapshot = candidate as Partial<LinkedPgaHitSnapshot>;
  if (
    typeof snapshot.id !== 'string' ||
    typeof snapshot.code !== 'string' ||
    typeof snapshot.listName !== 'string' ||
    typeof snapshot.agency !== 'string' ||
    typeof snapshot.linkedAt !== 'string'
  ) {
    return null;
  }
  return snapshot as LinkedPgaHitSnapshot;
};
